import Ajv, { _ } from 'ajv';
import AjvFormats from 'ajv-formats';
import AjvKeywords from 'ajv-keywords';
import standaloneCode from 'ajv/dist/standalone';
import { uid } from './keywords/uid';
import { uidv2 } from './keywords/uidv2';
import { uniqueItemProperties } from './keywords/uniqueItemProperties';
export const ajv = new Ajv({ allowUnionTypes: true, $data: true, addUsedSchema: false, strictNumbers: false });
AjvFormats(ajv);
// additional keywords
AjvKeywords(ajv); // add ajv-keywords (e.g. instanceof)
ajv.addKeyword(uid);
export function validate(schema, data) {
    const valid = ajv.validate(schema, data);
    if (!valid) {
        const errors = ajv.errorsText(ajv.errors, { separator: '.' });
        throw new Error(errors);
    }
}
export function compile(schema) {
    const validate = ajv.compile(schema);
    return (data) => {
        const valid = validate(data);
        if (!valid) {
            // I'm not sure the latter case can happen but types allow it, we'll generate a useful generic error just in case
            const errorMessage = validate.errors
                ? ajv.errorsText(validate.errors)
                : `Failed to validate ${JSON.stringify(data, null, 2)} against ${JSON.stringify(schema, null, 2)}`;
            throw new Error(errorMessage);
        }
    };
}
const standaloneCommonCode = new Map([
    [
        'ucs2length',
        String.raw `function ucs2length(str){const len=str.length;let length=0;let pos=0;let value;while(pos<len){length++;value=str.charCodeAt(pos++);if(value>=0xd800&&value<=0xdbff&&pos<len){value=str.charCodeAt(pos);if((value&0xfc00)===0xdc00)pos++}}return length}`
    ],
    [
        'equal',
        String.raw `function ajvEqual(a,b){if(a===b)return true;if(a&&b&&typeof a=="object"&&typeof b=="object"){if(a.constructor!==b.constructor)return false;var length,i,keys;if(Array.isArray(a)){length=a.length;if(length!=b.length)return false;for(i=length;i--!==0;)if(!ajvEqual(a[i],b[i]))return false;return true}if(a.constructor===RegExp)return a.source===b.source&&a.flags===b.flags;if(a.valueOf!==Object.prototype.valueOf)return a.valueOf()===b.valueOf();if(a.toString!==Object.prototype.toString)return a.toString()===b.toString();keys=Object.keys(a);length=keys.length;if(length!==Object.keys(b).length)return false;for(i=length;i--!==0;)if(!Object.prototype.hasOwnProperty.call(b,keys[i]))return false;for(i=length;i--!==0;){var key=keys[i];if(!ajvEqual(a[key],b[key]))return false}return true}return a!==a&&b!==b}`
    ],
    [
        'base64url-toBuffer',
        String.raw `function padString(input){var segmentLength=4;var stringLength=input.length;var diff=stringLength%segmentLength;if(!diff){return input}var position=stringLength;var padLength=segmentLength-diff;var paddedStringLength=stringLength+padLength;var buffer=Buffer.alloc(paddedStringLength);buffer.write(input);while(padLength--){buffer.write("=",position++)}return buffer.toString()}function toBase64(base64url){base64url=base64url.toString();return padString(base64url).replace(/\-/g,"+").replace(/_/g,"/")}function base64urlToBuffer(base64url){return Buffer.from(toBase64(base64url),"base64")}`
    ],
    [
        'base64url-encode',
        String.raw `function fromBase64(base64){return base64.replace(/=/g,"").replace(/\+/g,"-").replace(/\//g,"_")}function base64urlEncode(input,encoding){if(encoding===void 0){encoding="utf8"}if(Buffer.isBuffer(input)){return fromBase64(input.toString("base64"))}return fromBase64(Buffer.from(input,encoding).toString("base64"))}`
    ]
]);
/**
 * For formatting, AJV will by default "require('ajv/dist/runtime/ucs2length').default" and "require('ajv-formats/dist/formats').fullFormats"
 * for formats it does not natively support.
 *
 * That won't work for our validations.generated.ts files because they also need to work in the browser and also webpack won't maintain those paths.
 *
 * By using `import` here, we will force webpack to bundle the formats code but reference as a regular object.
 *
 * ref: https://ajv.js.org/guide/formats.html#formats-and-standalone-validation-code
 */
const standaloneImportsCode = [
    'import { fullFormats } from "ajv-formats/dist/formats";',
    'const formatWrapper = { ...fullFormats }',
    ''
];
const standaloneReplacers = [
    [`require("ajv/dist/runtime/ucs2length").default`, 'ucs2length', 'ucs2length'],
    [`require("ajv/dist/runtime/equal").default`, 'ajvEqual', 'equal'],
    [`require("base64url").default.toBuffer`, 'base64urlToBuffer', 'base64url-toBuffer'],
    [`require("base64url").default.encode`, 'base64urlEncode', 'base64url-encode']
];
const standaloneCodeExceptions = "/* eslint-disable */\n// @ts-nocheck\n// THIS FILE IS GENERATED, DO NOT EDIT\n// Regenerate the file by running 'yarn compile:ajv'\n\n";
const standaloneCodeHeader = `"use strict";`;
export function standalone(allSchemas) {
    const instance = new Ajv({
        allowUnionTypes: true,
        $data: true,
        addUsedSchema: false,
        strictNumbers: false,
        code: { esm: true, source: true, formats: _ `formatWrapper` },
        schemas: allSchemas
    });
    AjvFormats(instance);
    instance.addKeyword(uidv2);
    instance.addKeyword(uniqueItemProperties);
    let code = standaloneCode(instance);
    const usedCommonCode = new Set();
    for (let i = 0; i < standaloneReplacers.length; i++) {
        const [search, replace, commonCode] = standaloneReplacers[i];
        let index = code.indexOf(search);
        while (index > 0) {
            code = code.substring(0, index) + replace + code.substring(index + search.length);
            usedCommonCode.add(commonCode);
            index = code.indexOf(search);
        }
    }
    const allCommonCode = ['', ...standaloneImportsCode, ''].join(';') +
        [...usedCommonCode].map((c) => standaloneCommonCode.get(c)).join(';');
    if (code.startsWith(standaloneCodeHeader)) {
        code = standaloneCodeHeader + allCommonCode + code.substring(standaloneCodeHeader.length);
    }
    else {
        code = allCommonCode + code;
    }
    return standaloneCodeExceptions + code;
}
//# sourceMappingURL=validate.js.map